import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
} from "@mui/material";
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  Users,
  Globe,
  Smartphone,
  BarChart as BarChartIcon,
  TrendingUp,
} from "lucide-react";

const POLLING_INTERVAL = 10000;
const API_BASE_URL = "";
const COLORS = {
  primary: "#2E4053",
  secondary: "#A6AAAD",
  background: "#D1D8E0",
  textPrimary: "#34495E",
  accent1: "#5D6D7E",
  accent2: "#85929E",
};
import config from "../../src/config";

const baseUrl = config.baseURL;
const fetchWithRetry = async (url, retryCount = 0) => {
  const MAX_RETRIES = 3;
  const DELAY_MS = 1000 * Math.pow(2, retryCount);
  const token = localStorage.getItem("token"); // 🔥 Get token

  try {
    const response = await fetch(API_BASE_URL + url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`, // 🔥 Add this
      },
    });

    if (!response.ok) {
      throw new Error(
        `HTTP error! status: ${response.status} (${response.statusText})`
      );
    }

    const result = await response.json();

    if (result.success) {
      return result.data;
    } else {
      throw new Error(
        result.error || "Backend request succeeded but reported an error."
      );
    }
  } catch (error) {
    if (retryCount < MAX_RETRIES) {
      await new Promise((resolve) => setTimeout(resolve, DELAY_MS));
      return fetchWithRetry(url, retryCount + 1);
    } else {
      throw error;
    }
  }
};

const DashboardCard = ({
  title,
  icon: Icon,
  children,
  isLoading,
  subtitle,
}) => (
  <Paper
    sx={{
      // Set a fixed height for layout consistency across all cards
      height: "500px",
      display: "flex",
      flexDirection: "column",
      padding: "24px", // p-6 (for padding around the card)
      transition: "box-shadow 0.3s ease-in-out",
      "&:hover": {
        boxShadow: "0 10px 15px rgba(0, 0, 0, 0.15)", // Equivalent to hover:shadow-xl
      },
      backgroundColor: "white",
      borderRadius: "12px",
      boxShadow: "0 6px 10px rgba(0, 0, 0, 0.08)",
    }}
  >
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        marginBottom: "20px", // mb-5
        paddingBottom: "12px", // pb-3
        borderBottom: `1px solid ${COLORS.background}`,
        borderColor: COLORS.background,
      }}
    >
      {Icon && (
        <Icon
          size={24}
          color={COLORS.primary}
          style={{ marginRight: "12px" }} // mr-3
        />
      )}
      <Box>
        <Typography
          variant="h6"
          sx={{ color: COLORS.textPrimary, fontWeight: "bold" }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="caption" sx={{ color: COLORS.secondary }}>
            {subtitle}
          </Typography>
        )}
      </Box>
    </Box>
    <Box
      // This content box fills the remaining height of the card
      sx={{
        width: "100%",
        flexGrow: 1,
        position: "relative",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        // Removed padding as it's handled by the parent Paper now
      }}
    >
      {isLoading ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: "32px", // p-8
          }}
        >
          <CircularProgress sx={{ color: COLORS.primary }} />
          <Typography
            variant="body2"
            sx={{ marginTop: "8px", color: COLORS.textPrimary }} // mt-2
          >
            Loading Real-time Data...
          </Typography>
        </Box>
      ) : (
        // Inner Box for children must also fill space
        <Box
          sx={{
            width: "100%",
            flexGrow: 1,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {children}
        </Box>
      )}
    </Box>
  </Paper>
);

const LiveUsersTable = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px", // p-8
          height: "100%",
          color: COLORS.secondary,
          flexGrow: 1,
        }}
      >
        <Users size={40} style={{ marginBottom: "8px" }} />
        <Typography variant="body1">No live users to display.</Typography>
      </Box>
    );
  }

  return (
    <TableContainer
      // Max height is now 100% of the parent container, which is constrained by DashboardCard
      sx={{ maxHeight: "100%", overflow: "auto", flexGrow: 1 }}
    >
      <Table stickyHeader size="small">
        <TableHead>
          <TableRow sx={{ backgroundColor: COLORS.background }}>
            {["User", "Company", "Page", "Last Seen (s ago)"].map((header) => (
              <TableCell
                key={header}
                sx={{
                  fontWeight: "600", // font-semibold
                  color: COLORS.textPrimary,
                  borderBottom: `2px solid ${COLORS.secondary}`,
                }}
              >
                {header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.map((user) => (
            <TableRow key={user.session_id} hover>
              <TableCell sx={{ color: COLORS.textPrimary }}>
                {user.user_name}
              </TableCell>
              <TableCell sx={{ color: COLORS.textPrimary }}>
                {user.company_name}
              </TableCell>
              <TableCell
                sx={{ color: COLORS.textPrimary, fontSize: "0.75rem" }}
              >
                {user.page}
              </TableCell>
              <TableCell sx={{ color: COLORS.textPrimary }}>
                {user.last_seen
                  ? Math.floor(
                      (Date.now() - new Date(user.last_seen).getTime()) / 1000
                    )
                  : "N/A"}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const DeviceActivityChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px", // p-8
          height: "100%",
          color: COLORS.secondary,
          flexGrow: 1,
        }}
      >
        <Smartphone size={40} style={{ marginBottom: "8px" }} />
        <Typography variant="body1">No device activity data.</Typography>
      </Box>
    );
  }

  const chartColors = [COLORS.primary, COLORS.secondary, COLORS.accent1];
  return (
    <ResponsiveContainer width="100%" height="100%">
      <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
        <Pie
          data={data}
          dataKey="count"
          nameKey="device"
          cx="50%"
          cy="50%"
          outerRadius={100}
          fill={COLORS.primary}
          labelLine={false}
          label={({ device, percent }) =>
            `${device}: ${(percent * 100).toFixed(0)}%`
          }
        >
          {data.map((entry, index) => (
            <Cell
              key={`cell-${index}`}
              fill={chartColors[index % chartColors.length]}
            />
          ))}
        </Pie>
        <Tooltip
          formatter={(value, name, props) => [
            `${value} Sessions`,
            props.payload.device,
          ]}
        />
        <Legend layout="horizontal" align="center" verticalAlign="bottom" />
      </PieChart>
    </ResponsiveContainer>
  );
};

const MostUsedModulesChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px", // p-8
          height: "100%",
          color: COLORS.secondary,
          flexGrow: 1,
        }}
      >
        <BarChartIcon size={40} style={{ marginBottom: "8px" }} />
        <Typography variant="body1">No module usage data recorded.</Typography>
      </Box>
    );
  }

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data.sort((a, b) => b.hits - a.hits)}
        margin={{ top: 5, right: 10, left: -10, bottom: 5 }}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={COLORS.background} />
        <XAxis
          dataKey="page"
          tick={{ fill: COLORS.textPrimary, fontSize: 10 }}
          interval={0}
          angle={-45}
          textAnchor="end"
          height={70}
        />
        <YAxis tick={{ fill: COLORS.textPrimary }} />
        <Tooltip formatter={(value) => [`${value} Hits`, "Module Usage"]} />
        <Legend />
        <Bar dataKey="hits" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
};

const CompanyUsageSummary = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px", // p-8
          height: "100%",
          color: COLORS.secondary,
          flexGrow: 1,
        }}
      >
        <TrendingUp size={40} style={{ marginBottom: "8px" }} />
        <Typography variant="body1">
          No company usage data available.
        </Typography>
      </Box>
    );
  }

  const totalLogins = data.reduce((sum, item) => sum + item.total_logins, 0);

  return (
    <Box sx={{ paddingTop: "8px" }}>
      {" "}
      {/* pt-2 */}
      {data
        .sort((a, b) => b.total_logins - a.total_logins)
        .map((company, index) => {
          const percentage =
            totalLogins > 0 ? (company.total_logins / totalLogins) * 100 : 0;
          return (
            <div
              key={company.company_id}
              style={{ width: "100%", marginBottom: "16px" }} // space-y-4 margin approximation
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  fontSize: "0.875rem", // text-sm
                  marginBottom: "4px", // mb-1
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.textPrimary, fontWeight: "500" }} // font-medium
                >
                  {company.company_name}
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: COLORS.textPrimary, fontWeight: "600" }} // font-semibold
                >
                  {company.total_logins} ({percentage.toFixed(1)}%)
                </Typography>
              </div>
              <div
                style={{
                  width: "100%",
                  backgroundColor: "#e5e7eb", // bg-gray-200
                  borderRadius: "9999px", // rounded-full
                  height: "10px", // h-2.5
                }}
              >
                <div
                  style={{
                    height: "10px", // h-2.5
                    borderRadius: "9999px", // rounded-full
                    width: `${percentage}%`,
                    backgroundColor:
                      index === 0 ? COLORS.primary : COLORS.accent2,
                    transition: "width 0.5s ease-in-out",
                  }}
                ></div>
              </div>
            </div>
          );
        })}
    </Box>
  );
};

const SessionMap = ({ data }) => {
  const cityCounts = (data || []).reduce((acc, session) => {
    if (session.city && session.country) {
      const key = `${session.city}, ${session.country}`;
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});

  const topCities = Object.entries(cityCounts)
    .map(([location, count]) => ({ location, count }))
    .sort((a, b) => b.count - a.count)
    .slice(0, 5);

  if (topCities.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          padding: "32px", // p-8
          height: "100%",
          color: COLORS.secondary,
          flexGrow: 1,
        }}
      >
        <Globe size={40} style={{ marginBottom: "8px" }} />
        <Typography variant="body1">
          No geographical session data available.
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ paddingTop: "8px" }}>
      {" "}
      {/* pt-2, space-y-3 handled by children margins */}
      <Typography
        variant="body2"
        sx={{
          color: COLORS.textPrimary,
          fontWeight: "bold",
          marginBottom: "12px",
        }}
      >
        Top Active Locations (Cities, Countries)
      </Typography>
      <ul
        style={{
          listStyleType: "disc",
          listStylePosition: "inside",
          paddingLeft: "16px", // pl-4
          margin: 0,
          padding: 0, // Reset default ul padding
        }}
      >
        {topCities.map((city, index) => (
          <li
            key={index}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              fontSize: "0.875rem", // text-sm
              color: COLORS.textPrimary,
              marginBottom: "8px", // space-y-2
              marginLeft: "16px",
            }}
          >
            <span style={{ display: "flex", alignItems: "center" }}>
              <Globe
                size={16}
                color={COLORS.accent1}
                style={{ marginRight: "12px", display: "inline-block" }} // mr-3 inline-block
              />
              {city.location}
            </span>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "0.75rem", // text-xs
                padding: "6px", // p-1.5
                borderRadius: "4px", // rounded
                backgroundColor: COLORS.background,
                color: COLORS.primary,
              }}
            >
              {city.count} Sessions
            </span>
          </li>
        ))}
      </ul>
      <Typography
        variant="caption"
        sx={{ color: COLORS.secondary, display: "block", paddingTop: "12px" }} // block pt-3
      >
        Real-time geographical session data aggregated by city.
      </Typography>
    </Box>
  );
};

const MasterDashboard = () => {
  const [liveUsers, setLiveUsers] = useState([]);
  const [moduleUsage, setModuleUsage] = useState([]);
  const [companyUsage, setCompanyUsage] = useState([]);
  const [sessionMapData, setSessionMapData] = useState([]);
  const [deviceActivity, setDeviceActivity] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchAllData = useCallback(async () => {
    const endpoints = {
      liveUsers: `${baseUrl}/api/master/live-users`,
      moduleUsage: `${baseUrl}/api/master/most-used-modules`,
      companyUsage: `${baseUrl}/api/master/company-usage`,
      sessionMapData: `${baseUrl}/api/master/session-map`,
      deviceActivity: `${baseUrl}/api/master/device-activity`,
    };

    try {
      const results = await Promise.all(
        Object.values(endpoints).map((url) => fetchWithRetry(url))
      );

      const [
        liveUsersData,
        moduleUsageData,
        companyUsageData,
        sessionMapData,
        deviceActivityData,
      ] = results;

      setLiveUsers(liveUsersData || []);
      setModuleUsage(moduleUsageData || []);
      setCompanyUsage(companyUsageData || []);
      setSessionMapData(sessionMapData || []);
      setDeviceActivity(deviceActivityData || []);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  }, []);
  const fetchStaticData = async () => {
    await Promise.all([
      fetchMostUsedModules(),
      fetchCompanyUsage(),
      fetchDeviceActivity(),
    ]);
  };

  const fetchLiveData = async () => {
    await Promise.all([fetchLiveUsers(), fetchSessionMap()]);
  };
  useEffect(() => {
    let intervalId = null;

    const startPolling = () => {
      if (!intervalId) {
        intervalId = setInterval(fetchLiveData, 15000);
      }
    };

    const stopPolling = () => {
      if (intervalId) {
        clearInterval(intervalId);
        intervalId = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopPolling();
      } else {
        fetchLiveData(); // immediate refresh on focus
        startPolling();
      }
    };

    // 🚀 Initial load
    fetchStaticData(); // heavy data ONCE
    fetchLiveData(); // live data immediately
    startPolling();

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopPolling();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [fetchLiveData, fetchStaticData]);

  // useEffect(() => {
  //   fetchAllData();

  //   const intervalId = setInterval(fetchAllData, POLLING_INTERVAL);

  //   return () => clearInterval(intervalId);
  // }, [fetchAllData]);

  const totalUsers = liveUsers ? liveUsers.length : 0;

  return (
    <Box
      style={{ minHeight: "100vh", padding: "20px" }}
      sx={{
        backgroundColor: COLORS.background,
        fontFamily: "Inter, sans-serif",
      }}
    >
      <Box sx={{ marginBottom: "40px" }}>
        {" "}
        {/* mb-10 */}
        <Typography
          variant="h4"
          sx={{ color: COLORS.primary, fontWeight: "800" }} // font-extrabold
        >
          Master Dashboard Analytics
        </Typography>
        <Typography variant="subtitle1" sx={{ color: COLORS.textPrimary }}>
          Real-time activity and usage summary (
          {new Date().toLocaleTimeString()})
        </Typography>
      </Box>

      <Grid container spacing={5}>
        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            sx={{
              padding: "20px", // p-5
              borderRadius: "12px", // rounded-xl
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: COLORS.primary,
              color: "white",
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Online Users
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {totalUsers}
              </Typography>
            </Box>
            <Users size={48} sx={{ opacity: 0.7 }} />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            sx={{
              padding: "20px",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: COLORS.accent1,
              color: "white",
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Total Tracked Sessions
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: "bold" }}>
                {sessionMapData.length}
              </Typography>
            </Box>
            <Globe size={48} sx={{ opacity: 0.7 }} />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            sx={{
              padding: "20px",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: COLORS.secondary,
              color: COLORS.textPrimary,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Top Module
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                }}
              >
                {moduleUsage.length > 0
                  ? moduleUsage.sort((a, b) => b.hits - a.hits)[0].page
                  : "N/A"}
              </Typography>
            </Box>
            <TrendingUp size={48} sx={{ opacity: 0.7 }} />
          </Paper>
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <Paper
            sx={{
              padding: "20px",
              borderRadius: "12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              backgroundColor: COLORS.background,
              color: COLORS.primary,
              border: `1px solid ${COLORS.secondary}`,
            }}
          >
            <Box>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>
                Top Company (Logins)
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: "bold",
                  textOverflow: "ellipsis",
                  overflow: "hidden",
                  whiteSpace: "nowrap",
                  maxWidth: "150px",
                }}
              >
                {companyUsage.length > 0
                  ? companyUsage.sort(
                      (a, b) => b.total_logins - a.total_logins
                    )[0].company_name
                  : "N/A"}
              </Typography>
            </Box>
            <BarChartIcon size={48} sx={{ opacity: 0.7 }} />
          </Paper>
        </Grid>

        <Grid item xs={12} lg={6}>
          <DashboardCard
            title="Live Online Users"
            subtitle={`Total: ${totalUsers} active sessions`}
            icon={Users}
            isLoading={isLoading}
          >
            <LiveUsersTable data={liveUsers} />
          </DashboardCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <DashboardCard
            title="Session Map Overview"
            subtitle="Geographical distribution of active sessions"
            icon={Globe}
            isLoading={isLoading}
          >
            <SessionMap data={sessionMapData} />
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Device Activity"
            subtitle="Breakdown by connection device type"
            icon={Smartphone}
            isLoading={isLoading}
          >
            <DeviceActivityChart data={deviceActivity} />
          </DashboardCard>
        </Grid>

        <Grid item xs={12} md={6} lg={4}>
          <DashboardCard
            title="Most Used Modules"
            subtitle="Top 5 paths based on usage hits"
            icon={BarChartIcon}
            isLoading={isLoading}
          >
            <MostUsedModulesChart data={moduleUsage} />
          </DashboardCard>
        </Grid>

        <Grid item xs={12} lg={4}>
          <DashboardCard
            title="Company Usage Summary"
            subtitle="Total logins by company"
            icon={TrendingUp}
            isLoading={isLoading}
          >
            <CompanyUsageSummary data={companyUsage} />
          </DashboardCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MasterDashboard;
